/**
 * BANKS HOOK - API Methods & React Query Hooks Summary
 *
 * ===========================================
 * API METHODS (Internal Functions):
 * ===========================================
 * • getSystemBankDetails()     - GET /bank-details/get-system-details - Get system bank details by currency
 * • getBankDetailsPaginate()   - GET /bank-details - Get paginated bank details with filters
 * • storeBankDetails()         - POST /bank-details - Store bank details (IBAN/Account Number)
 * • viewBankDetails()          - GET /bank-details/:id - View specific bank details by ID
 * • paginateBankDetails()      - GET /bank-details - Get paginated bank details
 * • updateBankDetails()        - PUT /bank-details/:id - Update bank details by ID
 * • deleteBankDetails()         - DELETE /bank-details/:id - Delete bank details by ID
 * • getIbanDetails()           - GET /bank-details/get-iban-details - Get IBAN details and metadata
 *
 * ===========================================
 * REACT QUERY HOOKS (Exported):
 * ===========================================
 * • useSystemBankDetails(currencyCode?, filterKey?, enabled?) - Query: Get system bank details
 * • useBankDetailsPaginate(params?, enabled?)                 - Query: Get paginated bank details
 * • useStoreBankDetails()                                     - Mutation: Store bank details
 * • useViewBankDetails(id, enabled?)                          - Query: View bank details by ID
 * • usePaginateBankDetails(params?, enabled?)                 - Query: Paginate bank details
 * • useUpdateBankDetails()                                    - Mutation: Update bank details
 * • useDeleteBankDetails()                                    - Mutation: Delete bank details
 * • useIbanDetails(iban, enabled?)                           - Query: Get IBAN details and metadata
 *
 * ===========================================
 * KEY FEATURES:
 * ===========================================
 * • Authentication via Bearer token (store + cookie fallback)
 * • Support for both IBAN and Account Number formats
 * • Comprehensive filtering (holder, currency, country, account type)
 * • 30-minute stale time for system details (rarely change)
 * • 5-10 minute stale time for user bank details
 * • Automatic query invalidation on mutations
 * • Support for external/internal bank classification
 * • SWIFT, ABA routing number support for international transfers
 * ===========================================
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import fetchAPI from '@/lib/fetch-api';
import type { BankDetailsItem, SystemBankDetails } from '@/types/bank';

// ==================== INTERFACES ====================

interface Response {
  message: string;
  status: number;
  type: string;
}

interface BankDetailsPaginateParams {
  holder?: string;
  is_external?: boolean;
  currency_id?: number;
  country_id?: number;
  acc_type?: string;
  account_number?: string;
  iban?: string;
  search?: string;
  page?: number;
  per_page?: number;
  user_id?: number;
}

interface StoredBankDetails {
  id: number;
  is_external: boolean;
  iban: string;
  primary: boolean;
  address: string;
  name: string;
  account_number: number;
  swift: string | null;
  holder: string;
  holder_city: string;
  holder_address: string;
  holder_zip_code: number;
  phone: string;
  identifying_number: string | null;
  aba_routing_number: string | null;
  additional_fields?: BankDetailsItem['additional_fields'];
}

interface UpdateBankDetailsIbanRequest {
  holder: string;
  is_external: boolean;
  currency_id: number;
  acc_type: 'iban';
  iban: string;
  transfer_bank: boolean;
  holder_address: string;
  holder_city: string;
  holder_zip_code: string;
  holder_country_id: number;
}

export interface UpdateBankDetailsAccountNumberRequest {
  holder: string;
  is_external?: boolean;
  currency_id: number;
  acc_type: 'account_number';
  country_id: number;
  name: string;
  account_number: string;
  address: string;
  city: string;
  zip_code: string;
  holder_country_id: number;
  holder_city: string;
  holder_zip_code: string;
  holder_address?: string;
  swift?: string;
  routing_number?: string;
  sort_code?: string;
  ifsc?: string;
  abm_routing_code?: string;
  bank_branch_name?: string;
  cedula_id?: string;
  account_type?: string;
  personal_id_number?: string;
  phone?: string;
  identifying_number?: string;
}

export type UpdateBankDetailsRequest =
  | UpdateBankDetailsIbanRequest
  | UpdateBankDetailsAccountNumberRequest;

interface DeleteBankDetailsResponse extends Response {
  success: boolean;
}

interface PaginateBankDetailsRequest {
  holder?: string;
  is_external?: boolean;
  currency_id?: number;
  country_id?: number;
  acc_type?: 'iban' | 'account_number';
  account_number?: string;
  iban?: string;
  search?: string;
  page?: number;
  per_page?: number;
  is_salary_account?: boolean;
  user_id?: number;
}

interface PaginatedBankDetails {
  id: number;
  is_external: boolean;
  iban: string;
  primary: boolean;
  address: string;
  name: string;
  account_number: number;
  swift: string | null;
  holder: string;
  holder_city: string;
  holder_address: string;
  holder_zip_code: number;
  phone: string;
  identifying_number: string | null;
  aba_routing_number: string | null;
}

interface BankDetailsIbanRequest {
  holder: string;
  is_external?: boolean;
  currency_id: number;
  acc_type: 'iban';
  holder_address: string;
  holder_city: string;
  holder_zip_code: string;
  holder_country_id: string;
  iban: string;
  user_id?: number;
}

interface BankDetailsAccountNumberRequest {
  holder: string;
  is_external?: boolean;
  currency_id: number;
  acc_type: 'account_number';
  country_id: number;
  name: string;
  account_number: string;
  address: string;
  city: string;
  zip_code: string;
  identifying_number?: string;
  holder_country_id: number;
  holder_city: string;
  holder_zip_code: string;
  holder_address?: string;
  phone?: string;
  swift?: string;
  routing_number?: string;
  sort_code?: string;
  ifsc?: string;
  abm_routing_code?: string;
  bank_branch_name?: string;
  cedula_id?: string;
  account_type?: string;
  personal_id_number?: string;
  primary?: number;
  user_id?: number;
}

type BankDetailsStoreRequest = BankDetailsIbanRequest | BankDetailsAccountNumberRequest;

interface IbanDetails {
  iban: string;
  country_code: string;
  iso_alpha3: string;
  country_name: string;
  currency_code: string;
  sepa_member: string;
  sepa?: Array<{
    sepa_credit_transfer: string;
    sepa_credit_transfer_inst: string;
    sepa_direct_debit: string;
    sepa_sdd_core: string;
    sepa_b2b: string;
    sepa_card_clearing: string;
  }>;
  bank_account: string;
  bank?: {
    bank_name: string;
    phone: string;
    address: string;
    bic: string;
    city: string;
    state: string;
    zip: string;
  };
}

// ==================== API FUNCTIONS ====================

/**
 * Gets system bank details for a specific currency or all currencies
 * @param {string} [currencyCode] - Optional currency code to get specific currency details
 * @param {string} [filterKey] - Optional filter key to get only specific data from details
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<Record<string, SystemBankDetails> | SystemBankDetails | unknown>} The bank details data
 */
const getSystemBankDetails = async (
  currencyCode?: string,
  filterKey?: string
): Promise<Record<string, SystemBankDetails> | SystemBankDetails | unknown> => {
  // Build query parameters
  const params = new URLSearchParams();
  if (currencyCode) {
    params.append('currency_code', currencyCode);
  }
  if (filterKey) {
    params.append('filterKey', filterKey);
  }

  const url = `/bank-details/get-system-details${params.toString() ? `?${params.toString()}` : ''}`;

  const response = await fetchAPI<Record<string, SystemBankDetails> | SystemBankDetails | unknown>(
    url,
    {
      method: 'GET',
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch bank details');
  }

  return response.data!;
};

/**
 * Gets paginated bank details with optional filtering
 * @param {BankDetailsPaginateParams} params - Optional parameters for filtering and pagination
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<BankDetailsItem[]>} The paginated bank details data
 */
const getBankDetailsPaginate = async (
  params: BankDetailsPaginateParams = {}
): Promise<BankDetailsItem[]> => {
  // Build query parameters
  const queryParams = new URLSearchParams();

  if (params.holder) queryParams.append('holder', params.holder);
  if (params.is_external !== undefined)
    queryParams.append('is_external', params.is_external.toString());
  if (params.currency_id) queryParams.append('currency_id', params.currency_id.toString());
  if (params.country_id) queryParams.append('country_id', params.country_id.toString());
  if (params.acc_type) queryParams.append('acc_type', params.acc_type);
  if (params.account_number) queryParams.append('account_number', params.account_number);
  if (params.iban) queryParams.append('iban', params.iban);
  if (params.search) queryParams.append('search', params.search);
  if (params.page) queryParams.append('page', params.page.toString());
  if (params.per_page) queryParams.append('per_page', params.per_page.toString());
  if (params.user_id) queryParams.append('user_id', params.user_id.toString());

  const url = `/bank-details${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const response = await fetchAPI<BankDetailsItem[]>(url, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch bank details');
  }

  return response.data!;
};

/**
 * Stores bank details (IBAN or Account Number)
 * @param {BankDetailsStoreRequest} data - The bank details data to store
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<BankDetailsItem>} The stored bank details data
 */
const storeBankDetails = async (data: BankDetailsStoreRequest): Promise<BankDetailsItem> => {
  const response = await fetchAPI<BankDetailsItem>('/bank-details', {
    method: 'POST',
    body: JSON.stringify(data),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to store bank details');
  }

  return response.data!;
};

/**
 * Views specific bank details by ID
 * @param {number} id - The bank detail ID to view
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<StoredBankDetails>} The bank details data
 */
const viewBankDetails = async (id: number): Promise<StoredBankDetails> => {
  const response = await fetchAPI<StoredBankDetails>(`/bank-details/${id}`, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to view bank details');
  }

  return response.data!;
};

/**
 * Gets paginated bank details with filtering options
 * @param {PaginateBankDetailsRequest} params - The pagination and filter parameters
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<PaginatedBankDetails[]>} The paginated bank details data
 */
const paginateBankDetails = async (
  params: PaginateBankDetailsRequest = {}
): Promise<PaginatedBankDetails[]> => {
  // Build query parameters
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, value.toString());
    }
  });

  const url = `/bank-details${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const response = await fetchAPI<PaginatedBankDetails[]>(url, {
    method: 'GET',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to paginate bank details');
  }

  return response.data!;
};

/**
 * Updates bank details by ID
 * @param {number} id - The bank detail ID to update
 * @param {UpdateBankDetailsRequest} bankData - The updated bank details
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<StoredBankDetails>} The updated bank details data
 */
const updateBankDetails = async (
  id: number,
  bankData: UpdateBankDetailsRequest
): Promise<StoredBankDetails> => {
  const response = await fetchAPI<StoredBankDetails>(`/bank-details/${id}`, {
    method: 'PUT',
    body: JSON.stringify(bankData),
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to update bank details');
  }

  return response.data!;
};

/**
 * Deletes bank details by ID
 * @param {number} id - The bank detail ID to delete
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<DeleteBankDetailsResponse>} The deletion response
 */
const deleteBankDetails = async (id: number): Promise<DeleteBankDetailsResponse> => {
  const response = await fetchAPI<DeleteBankDetailsResponse>(`/bank-details/${id}`, {
    method: 'DELETE',
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to delete bank details');
  }

  return response.data!;
};

/**
 * Gets IBAN details for the authenticated user
 * @param {string} iban - The IBAN to get details for
 * @throws {Error} If no authentication token is found or the API request fails
 * @returns {Promise<IbanDetails>} The IBAN details data
 */
const getIbanDetails = async (iban: string): Promise<IbanDetails> => {
  const response = await fetchAPI<IbanDetails>(
    `/bank-details/get-iban-details?iban=${encodeURIComponent(iban)}`,
    {
      method: 'GET',
    }
  );

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch IBAN details');
  }

  return response.data!;
};

// ==================== HOOKS ====================

/**
 * React Query hook to fetch system bank details
 * @param {string} [currencyCode] - Optional currency code to filter bank details by currency
 * @param {string} [filterKey] - Optional filter key to get only specific data from bank details
 * @param {boolean} [enabled=true] - Whether the query should be enabled/executed
 * @returns {UseQueryResult<Record<string, SystemBankDetails> | SystemBankDetails | unknown, Error>} Query result containing bank details data and loading/error states
 */
export function useSystemBankDetails(
  currencyCode?: string,
  filterKey?: string,
  enabled: boolean = true
): UseQueryResult<Record<string, SystemBankDetails> | SystemBankDetails | unknown, Error> {
  return useQuery({
    queryKey: ['bank-details', currencyCode, filterKey],
    queryFn: () => getSystemBankDetails(currencyCode, filterKey),
    enabled,
    staleTime: 1800000, // 30 minutes (30 * 60 * 1000)
    gcTime: 1800000, // Keep in cache for 30 minutes
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}

/**
 * React Query hook to fetch paginated bank details
 * @param {BankDetailsPaginateParams} params - Optional parameters for filtering and pagination
 * @param {boolean} [enabled=true] - Whether the query should be enabled/executed
 * @returns {UseQueryResult<BankDetailsItem[], Error>} Query result containing paginated bank details data and loading/error states
 */
export function useBankDetailsPaginate(
  params: BankDetailsPaginateParams = {},
  enabled: boolean = true
): UseQueryResult<BankDetailsItem[], Error> {
  return useQuery({
    queryKey: ['bank-details-paginate', params],
    queryFn: () => getBankDetailsPaginate(params),
    enabled,
    staleTime: 300000, // 5 minutes
    gcTime: 600000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    refetchOnMount: true,
    retry: 1, // Retry once on failure
  });
}

/**
 * React Query mutation hook to store bank details
 * @returns {UseMutationResult<BankDetailsItem, Error, BankDetailsStoreRequest, unknown>} Mutation result containing store function and loading/error states
 */
export function useStoreBankDetails(): UseMutationResult<
  BankDetailsItem,
  Error,
  BankDetailsStoreRequest
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: storeBankDetails,
    onSuccess: () => {
      // Invalidate send money step-recipient list (useInfiniteQuery with key ['bank-details', ...])
      queryClient.invalidateQueries({ queryKey: ['bank-details'] });
      queryClient.invalidateQueries({ queryKey: ['bank-details-paginate'] });
    },
  });
}

/**
 * React Query hook to view specific bank details by ID
 * @param {number} id - The bank detail ID to view
 * @param {boolean} [enabled=true] - Whether the query should be enabled/executed
 * @returns {UseQueryResult<StoredBankDetails, Error>} Query result containing bank details data and loading/error states
 */
export function useViewBankDetails(
  id: number,
  enabled: boolean = true
): UseQueryResult<StoredBankDetails, Error> {
  return useQuery({
    queryKey: ['bank-details', 'view', id],
    queryFn: () => viewBankDetails(id),
    enabled: enabled && id > 0,
    staleTime: 300000, // 5 minutes
  });
}

/**
 * React Query hook to paginate bank details
 * @param {PaginateBankDetailsRequest} params - The pagination and filter parameters
 * @param {boolean} [enabled=true] - Whether the query should be enabled/executed
 * @returns {UseQueryResult<PaginatedBankDetails[], Error>} Query result containing paginated bank details data and loading/error states
 */
export function usePaginateBankDetails(
  params: PaginateBankDetailsRequest = {},
  enabled: boolean = true
): UseQueryResult<PaginatedBankDetails[], Error> {
  return useQuery({
    queryKey: ['bank-details', 'paginate', params],
    queryFn: () => paginateBankDetails(params),
    enabled,
    staleTime: 300000, // 5 minutes
  });
}

/**
 * React Query mutation hook to update bank details
 * @returns {UseMutationResult<StoredBankDetails, Error, {id: number, data: UpdateBankDetailsRequest}, unknown>} Mutation result containing update function and loading/error states
 */
export function useUpdateBankDetails(): UseMutationResult<
  StoredBankDetails,
  Error,
  { id: number; data: UpdateBankDetailsRequest }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }) => updateBankDetails(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['bank-details'] });
      queryClient.invalidateQueries({ queryKey: ['bank-details', 'view', id] });
      queryClient.invalidateQueries({ queryKey: ['bank-details-paginate'] });
    },
  });
}

/**
 * React Query mutation hook to delete bank details
 * @returns {UseMutationResult<DeleteBankDetailsResponse, Error, number, unknown>} Mutation result containing delete function and loading/error states
 */
export function useDeleteBankDetails(): UseMutationResult<
  DeleteBankDetailsResponse,
  Error,
  number
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteBankDetails,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-details'] });
    },
  });
}

/**
 * React Query hook to fetch IBAN details
 * @param {string} iban - The IBAN to get details for
 * @param {boolean} [enabled=true] - Whether the query should be enabled/executed
 * @returns {UseQueryResult<IbanDetails, Error>} Query result containing IBAN details data and loading/error states
 */
export function useIbanDetails(
  iban: string,
  enabled: boolean = true
): UseQueryResult<IbanDetails, Error> {
  return useQuery({
    queryKey: ['iban-details', iban],
    queryFn: () => getIbanDetails(iban),
    enabled: enabled && !!iban,
    staleTime: 300000, // 5 minutes
    gcTime: 600000, // Keep in cache for 10 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
}
